/**
 * lib/icon.tsx — pack:slug icon renderer with future-pack extensibility.
 *
 * The Jobs page redesign (docs/product/jobs-and-activity.md §10) stores
 * icons on `job_templates.icon` and `positions.icon` as `pack:slug`
 * strings, e.g. `lucide:file-spreadsheet`. The pack prefix lets future
 * packs (heroicons, tabler, emoji, custom uploads) drop in without a
 * schema change — register them in ICON_PACKS below.
 *
 * Render-time fallback chain (called by Jobs-page card components):
 *   1. template.icon if present
 *   2. first matching Position's icon if present
 *   3. lucide:briefcase (the DEFAULT_ICON_REF below)
 *
 * Adding a new pack: implement a renderer function that takes a slug and
 * size and returns a React element, then add it to ICON_PACKS.
 */

import type { ComponentType, ReactElement, SVGProps } from 'react';
import {
  AlertTriangle,
  ArrowUpCircle,
  BarChart3,
  Book,
  BookOpen,
  Briefcase,
  Calculator,
  Calendar,
  CalendarClock,
  CalendarDays,
  CalendarX,
  ClipboardList,
  CreditCard,
  DollarSign,
  FileEdit,
  FilePlus,
  FileSignature,
  FileText,
  FileWarning,
  FolderSearch,
  Gavel,
  GraduationCap,
  Handshake,
  HardDrive,
  Headphones,
  HeartHandshake,
  HelpCircle,
  Landmark,
  Laptop,
  LineChart,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquareQuote,
  PenTool,
  Receipt,
  Scale,
  Search,
  Server,
  ServerCog,
  ShieldCheck,
  Siren,
  TrendingUp,
  User,
  UserCog,
  UserPlus,
  UserSearch,
  Users,
} from 'lucide-react';

type LucideComponent = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

// All Lucide icons seeded by the Jobs-page redesign. Add entries here as
// Linqage Ops Admin curates the catalog. Unknown slugs render the
// fallback (lucide:briefcase).
const LUCIDE: Record<string, LucideComponent> = {
  'alert-triangle': AlertTriangle,
  'arrow-up-circle': ArrowUpCircle,
  'bar-chart-3': BarChart3,
  book: Book,
  'book-open': BookOpen,
  briefcase: Briefcase,
  calculator: Calculator,
  calendar: Calendar,
  'calendar-clock': CalendarClock,
  'calendar-days': CalendarDays,
  'calendar-x': CalendarX,
  'clipboard-list': ClipboardList,
  'credit-card': CreditCard,
  'dollar-sign': DollarSign,
  'file-edit': FileEdit,
  'file-plus': FilePlus,
  'file-signature': FileSignature,
  'file-text': FileText,
  'file-warning': FileWarning,
  'folder-search': FolderSearch,
  gavel: Gavel,
  'graduation-cap': GraduationCap,
  handshake: Handshake,
  'hard-drive': HardDrive,
  headphones: Headphones,
  'heart-handshake': HeartHandshake,
  'help-circle': HelpCircle,
  landmark: Landmark,
  laptop: Laptop,
  'line-chart': LineChart,
  'list-checks': ListChecks,
  mail: Mail,
  megaphone: Megaphone,
  'message-square-quote': MessageSquareQuote,
  'pen-tool': PenTool,
  receipt: Receipt,
  scale: Scale,
  search: Search,
  server: Server,
  'server-cog': ServerCog,
  'shield-check': ShieldCheck,
  siren: Siren,
  'trending-up': TrendingUp,
  user: User,
  'user-cog': UserCog,
  'user-plus': UserPlus,
  'user-search': UserSearch,
  users: Users,
};

const DEFAULT_ICON_REF = 'lucide:briefcase';

/**
 * Pack registry. Each pack receives the slug and rendering props and
 * returns a React element. Future packs (heroicons, tabler, custom) plug
 * in here without changing the schema or callers.
 */
interface IconRenderProps {
  size?: number;
  className?: string;
  'aria-hidden'?: boolean;
}

const ICON_PACKS: Record<string, (slug: string, props: IconRenderProps) => ReactElement> = {
  lucide: (slug, props) => {
    const Comp = LUCIDE[slug] ?? LUCIDE.briefcase ?? Briefcase;
    return <Comp {...props} />;
  },
};

export interface IconProps {
  /** `pack:slug` reference, e.g. `lucide:file-spreadsheet`. Falls back to `lucide:briefcase` when null/unknown. */
  iconRef?: string | null;
  /** Optional ordered fallback chain (e.g. first Position's icon when template.icon is null). */
  fallbackRefs?: (string | null | undefined)[];
  size?: number;
  className?: string;
  ariaLabel?: string;
}

/**
 * Resolve a `pack:slug` icon reference (with optional fallback chain) and
 * render it via the registered pack. Decorative by default (aria-hidden);
 * pass `ariaLabel` to make it meaningful to assistive tech.
 */
export function Icon({ iconRef, fallbackRefs, size = 20, className, ariaLabel }: IconProps) {
  const ref = pickFirstValidRef([iconRef, ...(fallbackRefs ?? [])]) ?? DEFAULT_ICON_REF;
  const [pack = 'lucide', slug = 'briefcase'] = ref.split(':', 2);
  const renderer = ICON_PACKS[pack] ?? ICON_PACKS.lucide!;
  const props: IconRenderProps = { size };
  if (className) props.className = className;
  if (!ariaLabel) props['aria-hidden'] = true;
  const el = renderer(slug, props);
  if (ariaLabel) {
    return (
      <span role="img" aria-label={ariaLabel} className={className}>
        {el}
      </span>
    );
  }
  return el;
}

function pickFirstValidRef(refs: (string | null | undefined)[]): string | null {
  for (const r of refs) {
    if (typeof r === 'string' && /^[a-z_]+:[a-z0-9_-]+$/i.test(r)) return r;
  }
  return null;
}
